import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Download, Eye, FileText, Filter, Loader2, PackageCheck, RefreshCw, RotateCcw, ScanLine, Search, Truck, Undo2 } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';
import { MultiSelectFilter } from '@/components/ui/MultiSelectFilter';
import { RtoDetailDrawer } from './RtoDetailDrawer';
import { ReceiveDialog, HandoverDialog, RedispatchDialog } from './RtoActionDialogs';
import { toCommaList } from '@/utils/search-list';
import {
  AgeBadge, StatusChip, fmtDate, fmtDateTime, fullAddress, shortAddress,
  RECEIVABLE_STATUS_IDS, AT_WAREHOUSE_STATUS_IDS, type RtoMeta, type RtoRow, type RtoTab,
} from './rtoShared';

interface Filters {
  orderId: string; awb: string; q: string;
  courierIds: (string | number)[]; statusIds: (string | number)[]; merchantIds: (string | number)[]; branchIds: (string | number)[];
  orderDateFrom: string; orderDateTo: string; lastEventFrom: string; lastEventTo: string; receivedFrom: string; receivedTo: string;
  ageing: string;
  /** "Received at GT Warehouse" — a received DATE exists, which is not the same as any one status. */
  receivedOnly: boolean;
  /** Sent out again under a new waybill — a LINK, not a status. */
  redispatchedOnly: boolean;
}
const EMPTY: Filters = {
  orderId: '', awb: '', q: '', courierIds: [], statusIds: [], merchantIds: [], branchIds: [],
  orderDateFrom: '', orderDateTo: '', lastEventFrom: '', lastEventTo: '', receivedFrom: '', receivedTo: '', ageing: '',
  receivedOnly: false, redispatchedOnly: false,
};

/** Typing in a search box should not fire a query per keystroke. */
function useDebounced<T>(value: T, ms = 400): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function toParams(tab: RtoTab, f: Filters, extra: Record<string, any> = {}) {
  const p: Record<string, any> = { orderType: tab, ...extra };
  const text: Array<keyof Filters> = ['orderId', 'awb', 'q', 'orderDateFrom', 'orderDateTo', 'lastEventFrom', 'lastEventTo', 'receivedFrom', 'receivedTo', 'ageing'];
  for (const k of text) if (String(f[k] ?? '').trim()) p[k] = String(f[k]).trim();
  for (const k of ['courierIds', 'statusIds', 'merchantIds', 'branchIds'] as const) if (f[k].length) p[k] = f[k].join(',');
  if (f.receivedOnly) p.receivedOnly = '1';
  if (f.redispatchedOnly) p.redispatchedOnly = '1';
  return p;
}

/** One labelled value in a card. Keeps every card's columns the same height and width. */
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase text-slate-400 mb-0.5">{label}</div>
      <div className="text-slate-700 min-w-0">{children}</div>
    </div>
  );
}

export function RtoOrdersPage() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { data: meta } = useQuery<RtoMeta>({ queryKey: ['rto-meta'], queryFn: async () => (await api.get('/orders/rto/meta')).data });

  const tabs = useMemo(() => {
    const t: RtoTab[] = [];
    if (!meta || meta.permissions.viewB2B) t.push('B2B');
    if (!meta || meta.permissions.viewB2C) t.push('B2C');
    return t;
  }, [meta]);
  const urlTab = sp.get('tab') === 'B2B' ? 'B2B' : sp.get('tab') === 'B2C' ? 'B2C' : null;
  const tab: RtoTab = urlTab && tabs.includes(urlTab) ? urlTab : (tabs[0] ?? 'B2C');

  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [showFilters, setShowFilters] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' }>({ key: 'rtoInitiatedAt', dir: 'desc' });
  const [openId, setOpenId] = useState<string | null>(null);
  const [receiving, setReceiving] = useState<RtoRow | null>(null);
  const [handing, setHanding] = useState<RtoRow | null>(null);
  const [redispatching, setRedispatching] = useState<RtoRow | null>(null);
  const [exporting, setExporting] = useState(false);
  // CS bulk requests — ticked shipments, and the remark that goes with the request.
  const [picked, setPicked] = useState<string[]>([]);
  const [csRemarks, setCsRemarks] = useState('');

  const debounced = useDebounced(filters);
  useEffect(() => { setPage(1); }, [debounced, tab]);
  const set = <K extends keyof Filters>(k: K, v: Filters[K]) => setFilters((f) => ({ ...f, [k]: v }));

  const listParams = toParams(tab, debounced, { page, pageSize, sort: sort.key, dir: sort.dir });
  // Counters show the split across the WHOLE filtered set, so they ignore the status and ageing
  // pickers AND the two card selections. Leaving the card flags in meant clicking a card narrowed
  // the summary to that card's own rows: pressing "Re-dispatched" (0 orders so far) made EVERY
  // counter read 0, which looks like the page broke rather than like a filter being applied.
  const summaryParams = toParams(tab, {
    ...debounced, statusIds: [], ageing: '', receivedOnly: false, redispatchedOnly: false,
  });

  const { data: summary } = useQuery({
    queryKey: ['rto-summary', summaryParams], enabled: tabs.length > 0,
    queryFn: async () => (await api.get('/orders/rto/summary', { params: summaryParams })).data,
  });
  const { data: list, isLoading, isFetching } = useQuery<{ total: number; items: RtoRow[] }>({
    queryKey: ['rto-list', listParams], enabled: tabs.length > 0,
    queryFn: async () => (await api.get('/orders/rto', { params: listParams })).data,
    placeholderData: (prev) => prev,
  });

  const isMerchant = !!meta?.isMerchant;
  const { data: merchants } = useQuery({
    queryKey: ['merchants-light'], enabled: !!meta && !isMerchant,
    queryFn: async () => (await api.get('/merchants', { params: { pageSize: 1000 } })).data,
  });
  const { data: couriers } = useQuery({
    queryKey: ['couriers-light'],
    queryFn: async () => (await api.get('/couriers', { params: { pageSize: 500 } })).data,
  });
  const merchantOptions = ((merchants as any)?.items ?? merchants ?? []).map((m: any) => ({ value: m.id, label: m.brandName || m.companyName, hint: m.brandName && m.brandName !== m.companyName ? m.companyName : undefined }));
  const courierOptions = ((couriers as any)?.items ?? couriers ?? []).filter((c: any) => c.orderType === tab).map((c: any) => ({ value: c.id, label: c.name }));

  /**
   * Open the handover proof in a new tab.
   *
   * Fetched on demand: the link is signed and short-lived, so it cannot sit in the page waiting to
   * be clicked. `noopener` because the new tab is handed a storage URL and has no business reaching
   * back into this one.
   */
  const openProof = async (r: RtoRow) => {
    try {
      const { url } = (await api.get(`/orders/rto/${r.id}/proof-url`)).data as { url: string };
      window.open(url, '_blank', 'noopener');
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Could not open the POD');
    }
  };

  const refresh = () => { qc.invalidateQueries({ queryKey: ['rto-list'] }); qc.invalidateQueries({ queryKey: ['rto-summary'] }); };
  const canCsRequest = !!meta?.permissions.csRequest;
  /*
   * Which CS requests a row can still take (operator, 31-10). Only a parcel at a GT warehouse and not
   * yet handed back, and never the request it is ALREADY in — a shipment sitting on "RTO Delivered to
   * Merchant Requested" is offered "Re-dispatch Requested" only. Same rule the server applies
   * (decideCsRequest), so the screen never offers a button the server would refuse.
   */
  const csTargetsFor = (r: RtoRow): number[] =>
    !AT_WAREHOUSE_STATUS_IDS.includes(r.statusId) || r.merchantReturnDate
      ? []
      : (meta?.csRequestStatuses ?? []).map((st) => st.id).filter((id) => id !== r.statusId);
  const rowsById = useMemo(() => new Map((list?.items ?? []).map((r) => [r.id, r])), [list]);
  const selectable = (list?.items ?? []).filter((r) => csTargetsFor(r).length > 0);
  const eligibleFor = (status: number) => picked.filter((id) => { const r = rowsById.get(id); return !!r && csTargetsFor(r).includes(status); });
  // A tick is about a row on THIS list; a new filter, page or tab is a different list.
  useEffect(() => { setPicked([]); }, [debounced, tab, page, pageSize]);
  const csRequest = useMutation({
    mutationFn: async (status: number) => (await api.post('/orders/rto/cs-request', { ids: eligibleFor(status), status, remarks: csRemarks.trim() || undefined })).data,
    onSuccess: (r: any) => {
      if (r.updated) toast.success(`${r.updated} shipment(s) set to ${r.statusName}`);
      if (r.failed) {
        const why = (r.results ?? []).filter((x: any) => !x.ok).slice(0, 5).map((x: any) => `${x.awb ?? x.id}: ${x.message}`).join(' · ');
        toast.warning(`${r.failed} not updated — ${why}`);
      }
      setPicked([]); setCsRemarks('');
      refresh();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not update the shipments'),
  });
  const canExport = tab === 'B2B' ? meta?.permissions.exportB2B : meta?.permissions.exportB2C;
  const canScan = !!meta && !isMerchant && (meta.permissions.receive || meta.permissions.handover);
  const activeFilters = Object.entries(filters).some(([, v]) => (Array.isArray(v) ? v.length : String(v).trim()));

  async function doExport() {
    setExporting(true);
    try {
      const qs = new URLSearchParams(toParams(tab, filters) as any).toString();
      await downloadBlob(`/orders/rto/export?${qs}`, `rto-orders-${tab.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Export failed');
    } finally { setExporting(false); }
  }

  const counters: Array<{ key: string; label: string; value?: number; statusIds?: number[]; receivedOnly?: boolean; redispatchedOnly?: boolean; tone: string }> = [
    { key: 'total', label: 'Total RTO', value: summary?.total, tone: 'border-slate-200' },
    // Pending and In Transit are one figure: both mean "coming back, not here yet". Clicking it
    // filters to BOTH statuses, so the list still matches the number on the card.
    { key: 'pending', label: 'RTO Pending', value: summary?.rtoPending, statusIds: [18, 10], tone: 'border-amber-200' },
    { key: 'delivered', label: 'RTO Delivered', value: summary?.rtoDelivered, statusIds: [11, 12], tone: 'border-sky-200' },
    // In the warehouse NOW — exactly the status "RTO Received in GT Warehouse" (34), so the card, the
    // status filter and the export are one and the same list (operator, 30-10). It used to count every
    // parcel EVER received (rtoReceivedAt set), including ones since handed back or re-dispatched, so
    // its export disagreed with the status of the same name; the separate "Pending Merchant Return"
    // card that held the status-34 figure is folded into this one.
    { key: 'received', label: 'RTO Received in GT Warehouse', value: summary?.receivedAtWarehouse, statusIds: [34], tone: 'border-indigo-200' },
    // Came back damaged — photo / video and remarks on record, and a private ticket for CS.
    { key: 'damaged', label: 'Received at GT Warehouse (Damaged)', value: summary?.receivedDamaged, statusIds: [36], tone: 'border-rose-300' },
    // The two CS requests (operator, 24-10).
    { key: 'deliveryRequested', label: 'RTO Delivered to Merchant Requested', value: summary?.merchantDeliveryRequested, statusIds: [37], tone: 'border-teal-300' },
    { key: 'redispatchRequested', label: 'Re-dispatch Requested', value: summary?.redispatchRequested, statusIds: [38], tone: 'border-violet-300' },
    { key: 'handed', label: 'Delivered to Merchant', value: summary?.deliveredToMerchant, statusIds: [35], tone: 'border-green-200' },
    // Counted on the LINK, like "Received", so it needs its own filter rather than a status list.
    { key: 'redispatched', label: 'Re-dispatched', value: summary?.redispatched, redispatchedOnly: true, tone: 'border-indigo-300' },
  ];
  const statusFilterKey = [...filters.statusIds].map(Number).sort().join(',');

  /**
   * Sorting moved out of the column headers when the table became cards.
   *
   * The same four keys the headers offered, nothing added: a list people sort by ageing and by last
   * event is the whole reason this screen exists, and losing that to a layout change would be a
   * worse trade than the horizontal scrolling it replaced.
   */
  const SORTS: Array<{ key: string; label: string }> = [
    { key: 'rtoInitiatedAt', label: 'RTO ageing' },
    { key: 'lastEventDate', label: 'Last event' },
    { key: 'bookingDate', label: 'Order date' },
    { key: 'rtoReceivedAt', label: 'RTO received' },
  ];

  if (meta && !tabs.length) {
    return <div className="p-6 text-sm text-slate-600">You don't have access to RTO Orders. Ask an administrator to grant it under Users → Roles &amp; Permissions.</div>;
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><Undo2 size={22} /> RTO Orders</h1>
          <p className="text-sm text-slate-500">Returns from the courier, receipt at GT warehouse and handover back to the merchant</p>
        </div>
        <div className="flex items-center gap-2">
          {canExport && (
            <button onClick={doExport} disabled={exporting} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-50">
              {exporting ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Export
            </button>
          )}
          {canScan && (
            <button onClick={() => navigate('/rto-orders/scan')} className="px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium flex items-center gap-1.5">
              <ScanLine size={15} /> Scan RTO
            </button>
          )}
        </div>
      </div>

      <div className="inline-flex bg-slate-100 rounded-lg p-1 mb-4">
        {tabs.map((t) => (
          <button key={t} onClick={() => { setSp({ tab: t }, { replace: true }); setFilters((f) => ({ ...f, courierIds: [] })); }}
            className={`px-5 py-1.5 text-sm rounded-md ${tab === t ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>{t}</button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 xl:grid-cols-10 gap-2 mb-4">
        {counters.map((c) => {
          const active = c.receivedOnly ? filters.receivedOnly
            : c.redispatchedOnly ? filters.redispatchedOnly
            : !!c.statusIds && statusFilterKey === [...c.statusIds].sort().join(',');
          const clickable = !!c.statusIds || !!c.receivedOnly || !!c.redispatchedOnly || c.key === 'total';
          return (
            <button key={c.key} disabled={!clickable}
              onClick={() => {
                // Each card asks a different question, so picking one clears the others rather than
                // silently ANDing them and showing a number that matches no card on the screen.
                const clear = { statusIds: [] as (string | number)[], receivedOnly: false, redispatchedOnly: false };
                if (c.key === 'total') { setFilters((f) => ({ ...f, ...clear })); setPage(1); }
                else if (c.receivedOnly) { setFilters((f) => ({ ...f, ...clear, receivedOnly: !f.receivedOnly })); setPage(1); }
                else if (c.redispatchedOnly) { setFilters((f) => ({ ...f, ...clear, redispatchedOnly: !f.redispatchedOnly })); setPage(1); }
                else if (c.statusIds) { setFilters((f) => ({ ...f, ...clear, statusIds: active ? [] : c.statusIds! })); setPage(1); }
              }}
              className={`text-left bg-white border rounded-xl px-3 py-2.5 ${c.tone} ${active ? 'ring-2 ring-primary/40' : ''} ${c.statusIds || c.key === 'total' ? 'hover:shadow-sm' : 'cursor-default'}`}>
              <div className="text-xl font-bold text-slate-900">{c.value ?? '—'}</div>
              <div className="text-[11px] text-slate-600 leading-tight">{c.label}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl p-3 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
            {/* Spaces and line breaks between numbers turn into commas as you type or paste. */}
            <input value={filters.awb} onChange={(e) => set('awb', toCommaList(e.target.value))}
              placeholder={tab === 'B2B' ? 'LR / AWB — paste many, spaces become commas' : 'AWB — paste many, spaces become commas'}
              className="input w-full pl-8 font-mono" />
          </div>
          <input value={filters.q} onChange={(e) => set('q', e.target.value)} placeholder="Customer name or mobile" className="input min-w-[200px]" />
          <select value={filters.ageing} onChange={(e) => set('ageing', e.target.value)} className="input w-[150px]" title="Open returns only, days since RTO began">
            <option value="">Any ageing</option>
            {(meta?.ageingBuckets ?? []).map((b) => <option key={b.key} value={b.key}>{b.label}</option>)}
          </select>
          <select value={sort.key} onChange={(e) => setSort((x) => ({ ...x, key: e.target.value }))}
            className="input w-[150px]" title="Sort by">
            {SORTS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
          <button onClick={() => setSort((x) => ({ ...x, dir: x.dir === 'desc' ? 'asc' : 'desc' }))}
            className="px-3 py-2 border border-slate-200 rounded-lg text-sm hover:bg-slate-50"
            title={sort.dir === 'desc' ? 'Newest / oldest first — click for the other way' : 'Oldest / newest first — click for the other way'}>
            {sort.dir === 'desc' ? '\u2193' : '\u2191'}
          </button>
          <button onClick={() => setShowFilters((s) => !s)} className={`px-3 py-2 border rounded-lg text-sm flex items-center gap-1.5 ${showFilters ? 'border-primary text-primary' : 'border-slate-200'}`}>
            <Filter size={14} /> Filters
          </button>
          {activeFilters && (
            <button onClick={() => setFilters(EMPTY)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm flex items-center gap-1.5 hover:bg-slate-50">
              <RotateCcw size={14} /> Reset
            </button>
          )}
        </div>
        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 mt-3 pt-3 border-t border-slate-100">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Order ID</label>
              <input value={filters.orderId} onChange={(e) => set('orderId', e.target.value)} className="input w-full" />
            </div>
            <MultiSelectFilter label="RTO status" options={(meta?.statuses ?? []).map((s) => ({ value: s.id, label: s.name }))} values={filters.statusIds} onChange={(v) => set('statusIds', v)} />
            <MultiSelectFilter label="Courier" options={courierOptions} values={filters.courierIds} onChange={(v) => set('courierIds', v)} />
            {!isMerchant && <MultiSelectFilter label="Merchant" options={merchantOptions} values={filters.merchantIds} onChange={(v) => set('merchantIds', v)} />}
            {!isMerchant && <MultiSelectFilter label="GT warehouse (received at)" options={(meta?.branches ?? []).map((b) => ({ value: b.id, label: b.name }))} values={filters.branchIds} onChange={(v) => set('branchIds', v)} />}
            {([
              ['Order date', 'orderDateFrom', 'orderDateTo'],
              ['Last event date', 'lastEventFrom', 'lastEventTo'],
              ['RTO received date', 'receivedFrom', 'receivedTo'],
            ] as const).map(([label, a, b]) => (
              <div key={label}>
                <label className="text-xs text-slate-500 mb-1 block">{label}</label>
                <div className="flex gap-1.5">
                  <input type="date" value={filters[a]} onChange={(e) => set(a, e.target.value)} className="input w-full" />
                  <input type="date" value={filters[b]} onChange={(e) => set(b, e.target.value)} className="input w-full" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CS bulk bar — only for the CS team, and only while something is ticked. */}
      {canCsRequest && picked.length > 0 && (
        <div className="bg-primary/5 border border-primary/30 rounded-xl p-3 mb-3 flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-slate-700">{picked.length} selected</span>
          <input value={csRemarks} onChange={(e) => setCsRemarks(e.target.value)} placeholder="Remarks (optional)" className="input min-w-[220px] flex-1" />
          {(meta?.csRequestStatuses ?? []).map((st) => {
            // Only the requests the ticked shipments can still take; the count shows when some of
            // them are already in it.
            const n = eligibleFor(st.id).length;
            if (!n) return null;
            return (
              <button key={st.id} disabled={csRequest.isPending} data-testid={`cs-request-${st.id}`}
                onClick={() => { if (confirm(`Set ${n} shipment(s) to "${st.name}"?`)) csRequest.mutate(st.id); }}
                className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5">
                {csRequest.isPending && csRequest.variables === st.id && <Loader2 size={14} className="animate-spin" />} {st.name}{n < picked.length ? ` (${n})` : ''}
              </button>
            );
          })}
          {(meta?.csRequestStatuses ?? []).every((st) => !eligibleFor(st.id).length) && (
            <span className="text-xs text-slate-500">No request applies to the selected shipment(s).</span>
          )}
          <button onClick={() => setPicked([])} className="text-sm text-slate-500 hover:text-slate-800 underline ml-auto">Clear selection</button>
        </div>
      )}
      {canCsRequest && !!selectable.length && (
        <label className="flex items-center gap-2 text-xs text-slate-500 mb-2">
          <input type="checkbox" className="accent-primary"
            checked={selectable.every((r) => picked.includes(r.id))}
            onChange={(e) => setPicked(e.target.checked ? selectable.map((r) => r.id) : [])} />
          Select all on this page — CS: request a merchant delivery or a re-dispatch
        </label>
      )}

      <div className="space-y-2">
        {isLoading && <div className="bg-white border border-slate-200 rounded-xl py-12 text-center"><Loader2 className="mx-auto animate-spin text-slate-400" /></div>}
        {!isLoading && !(list?.items ?? []).length && (
          <div className="bg-white border border-slate-200 rounded-xl py-12 text-center text-slate-400">No RTO orders for these filters.</div>
        )}
        {(list?.items ?? []).map((r) => {
          const canReceive = !!meta?.permissions.receive && RECEIVABLE_STATUS_IDS.includes(r.statusId) && !r.rtoReceivedAt;
          /*
           * Hand back to the merchant, straight from the list.
           *
           * Offered once the parcel is actually AT a GT warehouse — before that there is nothing in
           * hand to give back — and hidden once it has already gone, so the button never promises an
           * action the server would refuse as already processed.
           */
          const canHandover = !!meta?.permissions.handover && (!!r.rtoReceivedAt || AT_WAREHOUSE_STATUS_IDS.includes(r.statusId)) && !r.merchantReturnDate;
          return (
            <div key={r.id} className="bg-white border border-slate-200 rounded-xl hover:border-slate-300 transition-colors">
              {/* HEAD — identity on the left, state on the right. The two things you scan a list for. */}
              <div className="flex flex-wrap items-start gap-x-4 gap-y-2 px-4 pt-3">
                {canCsRequest && csTargetsFor(r).length > 0 && (
                  <input type="checkbox" className="accent-primary mt-1.5" aria-label={`Select ${r.awb ?? r.orderNo}`}
                    checked={picked.includes(r.id)}
                    onChange={(e) => setPicked((p) => (e.target.checked ? [...p, r.id] : p.filter((x) => x !== r.id)))} />
                )}
                <div className="min-w-0 flex-1">
                  <button onClick={() => setOpenId(r.id)} className="text-primary hover:underline font-semibold text-left break-all" title={r.orderNo}>
                    {r.orderNo}
                  </button>
                  <div className="text-xs text-slate-500 flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-0.5">
                    <span className="font-mono text-slate-700 break-all">{r.awb}</span>
                    {r.courier && <><span className="text-slate-300">·</span><span className="truncate max-w-[16rem]" title={r.courier}>{r.courier}</span></>}
                    {!isMerchant && r.merchant && <><span className="text-slate-300">·</span><span className="truncate max-w-[16rem]" title={r.merchant}>{r.merchant}</span></>}
                  </div>
                  {/* Old → new: which order and waybill this return went out again as. */}
                  {r.redispatchedTo && (
                    <div className="text-[11px] mt-1 inline-flex flex-wrap items-center gap-1 px-2 py-0.5 rounded bg-indigo-50 text-indigo-800 border border-indigo-200">
                      <RefreshCw size={11} /> Re-dispatched as
                      <Link to={`/orders-${r.redispatchedTo.orderType.toLowerCase()}/${r.redispatchedTo.id}`} className="font-semibold hover:underline">{r.redispatchedTo.orderNo}</Link>
                      {r.redispatchedTo.awb && <span className="font-mono">· {r.redispatchedTo.awb}</span>}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <StatusChip id={r.statusId} name={r.statusName} hint={r.courierReportedOnly ? 'Courier reports it delivered back; not yet received at a GT warehouse' : undefined} />
                  <AgeBadge days={r.rtoAgeingDays} open={r.isOpen} label="RTO" />
                  {r.warehouseAgeingDays != null && <AgeBadge days={r.warehouseAgeingDays} open={r.isOpen} label="WH" />}
                </div>
                {/* Actions stay on one line at the end of the head, so they are in the same place on
                    every card however much the rest wraps. */}
                <div className="flex items-center gap-0.5 shrink-0">
                  <button onClick={() => setOpenId(r.id)} className="p-1.5 text-slate-500 hover:text-primary rounded hover:bg-slate-100" title="View details"><Eye size={15} /></button>
                  {canReceive && (
                    <button onClick={() => setReceiving(r)} className="p-1.5 text-slate-500 hover:text-emerald-700 rounded hover:bg-emerald-50" title="I Received This Order">
                      <PackageCheck size={15} />
                    </button>
                  )}
                  {canHandover && (
                    <button onClick={() => setHanding(r)} className="p-1.5 text-slate-500 hover:text-green-700 rounded hover:bg-green-50" title="Delivered to Merchant">
                      <Truck size={15} />
                    </button>
                  )}
                  {/* Send it out again under a new waybill. Offered on any returned shipment the user
                      may act on, and hidden once it has already been re-dispatched. */}
                  {!!meta?.permissions.handover && !r.redispatchedToOrderId && (
                    <button onClick={() => setRedispatching(r)} className="p-1.5 text-slate-500 hover:text-indigo-700 rounded hover:bg-indigo-50" title="Re-dispatch under a new AWB">
                      <RefreshCw size={15} />
                    </button>
                  )}
                </div>
              </div>

              {/* BODY — everything the table used to push off the right edge, on one screen. */}
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-x-4 gap-y-2 px-4 py-3 mt-2 border-t border-slate-100 text-xs">
                <Field label="Customer">
                  <div className="truncate" title={r.customerName ?? ''}>{r.customerName || '—'}</div>
                  {r.customerMobile && <div className="text-slate-500 font-mono">{r.customerMobile}</div>}
                </Field>
                <Field label="From">
                  <div className="truncate cursor-help" title={fullAddress(r.from)}>{shortAddress(r.from) || '—'}</div>
                </Field>
                <Field label="To">
                  <div className="truncate cursor-help" title={fullAddress(r.to)}>{shortAddress(r.to) || '—'}</div>
                </Field>
                <Field label="Order date">{fmtDate(r.orderDate) || '—'}</Field>
                <Field label="Last event">{fmtDateTime(r.lastEventDate) || '—'}</Field>
                <Field label="RTO received">
                  {r.rtoReceivedAt
                    ? <><div>{fmtDate(r.rtoReceivedAt)}</div><div className="text-slate-500 truncate" title={r.rtoReceivedBranch ?? ''}>{r.rtoReceivedBranch}</div></>
                    : '—'}
                </Field>
                {r.merchantReturnDate && (
                  <Field label="Merchant return">
                    <div>{fmtDate(r.merchantReturnDate)}</div>
                    {/* The handover proof, shown where the handover is recorded. Signed on click
                        rather than for every row — see `proofUrl`. Proof is optional on the bulk
                        scan path, so a row without one says so instead of offering a dead link. */}
                    {r.hasProof
                      ? <button type="button" onClick={() => openProof(r)}
                          className="text-primary hover:underline inline-flex items-center gap-1 mt-0.5">
                          <FileText size={11} /> View POD
                        </button>
                      : <span className="text-slate-400">No POD uploaded</span>}
                  </Field>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 mt-3">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 text-xs text-slate-600">
          <div className="flex items-center gap-2">
            {isFetching && !isLoading && <Loader2 size={12} className="animate-spin" />}
            {list ? `${((page - 1) * pageSize + 1).toLocaleString('en-IN')}–${Math.min(page * pageSize, list.total).toLocaleString('en-IN')} of ${list.total.toLocaleString('en-IN')}` : ''}
          </div>
          <div className="flex items-center gap-2">
            <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="input py-1 text-xs">
              {[25, 50, 100, 200].map((n) => <option key={n} value={n}>{n} / page</option>)}
            </select>
            <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Previous</button>
            <button disabled={!list || page * pageSize >= list.total} onClick={() => setPage((p) => p + 1)} className="px-2 py-1 border border-slate-200 rounded disabled:opacity-50">Next</button>
          </div>
        </div>
      </div>

      <RtoDetailDrawer id={openId} meta={meta} onClose={() => setOpenId(null)} onChanged={refresh} />
      <ReceiveDialog open={!!receiving} onClose={() => setReceiving(null)} meta={meta}
        order={receiving ? { id: receiving.id, awb: receiving.awb, statusName: receiving.statusName } : null} onDone={refresh} />
      {/* Same dialog the detail drawer uses, with the proof upload made optional — see HandoverDialog. */}
      <RedispatchDialog open={!!redispatching} onClose={() => setRedispatching(null)}
        order={redispatching ? { id: redispatching.id, awb: redispatching.awb, orderType: redispatching.orderType, merchant: redispatching.merchant } : null}
        onDone={refresh} />
      <HandoverDialog open={!!handing} onClose={() => setHanding(null)} proofOptional
        order={handing ? { id: handing.id, awb: handing.awb, orderType: handing.orderType, merchant: handing.merchant } : null} onDone={refresh} />
    </div>
  );
}
